import React, { useEffect } from 'react';
import { Link as RouterLink, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { logout } from '../../services/AuthService';
import { resetNotifications } from '../../redux/slices/notificationsSlice';
import useAuth from '../../common/hooks/useAuth';
import UserContainer from '../user/UserContainer';
import NotificationsContainer from '../notifications/NotificationsContainer';
import UserTalentContainer from '../userTalent/UserTalentContainer';
import LanguageSelector from '../common/LanguageSelector';
import AICareerAssistant from '../chat/AICareerAssistant';
import FooterComponent from '../footer/FooterComponent';
import { brand } from '../../theme';

const Logo = ({ compact }) => (
  <Box
    component={RouterLink}
    to="/"
    sx={{ display: 'flex', alignItems: 'center', gap: 1.2, textDecoration: 'none', color: 'inherit', minWidth: 0 }}
    aria-label="PlaceYourJob"
  >
    <Box
      component="img"
      src="/img/logo-96.png"
      alt=""
      width={compact ? 34 : 38}
      height={compact ? 34 : 38}
      sx={{ borderRadius: '11px', boxShadow: '0 6px 16px -6px rgba(15,23,42,0.45)', flexShrink: 0 }}
    />
    <Typography
      component="span"
      sx={{
        fontWeight: 800,
        fontSize: compact ? '1.05rem' : '1.2rem',
        letterSpacing: '-0.03em',
        whiteSpace: 'nowrap',
        display: { xs: compact ? 'none' : 'inline', sm: 'inline' },
      }}
    >
      PlaceYour<Box component="span" sx={{ background: brand.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Job</Box>
    </Typography>
  </Box>
);

export default function HeaderComponent({ children }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const handleLogout = () => {
    logout();
    dispatch(resetNotifications());
    navigate('/posts');
  };

  // Session expired / logged out elsewhere: drop the previous user's notifications.
  useEffect(() => {
    if (!isAuth) dispatch(resetNotifications());
  }, [isAuth, dispatch]);

  // Keep <html lang> in sync (screen readers, browser auto-translate prompts).
  const { i18n } = useTranslation();
  useEffect(() => {
    try { document.documentElement.lang = (i18n.language || 'en').split('-')[0]; } catch { /* ignore */ }
  }, [i18n.language]);

  const desktopLinks = [
    { label: t('nav.jobs'), to: '/posts', show: true },
    { label: t('nav.yourJobs'), to: '/your-posts', show: isAuth },
    { label: t('nav.favourites'), to: '/favourites', show: isAuth },
  ].filter((l) => l.show);

  const mobileItems = isAuth
    ? [
        { label: t('nav.jobs'), to: '/posts', icon: <SearchRoundedIcon />, match: ['/posts', '/jobs'] },
        { label: t('nav.favourites'), to: '/favourites', icon: <FavoriteBorderIcon /> },
        { label: t('nav.create'), to: '/add-post', icon: <AddRoundedIcon />, primary: true },
        { label: t('nav.yourJobs'), to: '/your-posts', icon: <WorkOutlineIcon /> },
      ]
    : [
        { label: t('nav.home'), to: '/', icon: <HomeOutlinedIcon />, exact: true },
        { label: t('nav.jobs'), to: '/posts', icon: <SearchRoundedIcon />, match: ['/posts', '/jobs'] },
        { label: t('nav.login'), to: '/login', icon: <LoginRoundedIcon /> },
        { label: t('nav.register'), to: '/register', icon: <PersonAddAltRoundedIcon /> },
      ];

  const activeMobile = mobileItems.findIndex((item) => {
    if (item.exact) return location.pathname === item.to;
    const prefixes = item.match || [item.to];
    return prefixes.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  });

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: alpha('#FFFFFF', 0.82),
          backdropFilter: 'saturate(180%) blur(18px)',
          WebkitBackdropFilter: 'saturate(180%) blur(18px)',
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(brand.border, 0.9)}`,
          paddingTop: 'var(--safe-top)',
          zIndex: (th) => th.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            minHeight: 'var(--app-header-height) !important',
            gap: { xs: 0.5, md: 2 },
            px: { xs: 'max(12px, var(--safe-left))', md: 3 },
            maxWidth: 1400,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Logo compact={isMobile && isAuth} />

          {!isMobile && (
            <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 3 }}>
              {desktopLinks.map((link) => (
                <Button
                  key={link.to}
                  component={NavLink}
                  to={link.to}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    px: 1.8,
                    '&.active': { color: 'primary.main', backgroundColor: alpha(brand.primary, 0.08) },
                    '&:hover': { backgroundColor: alpha(brand.primary, 0.06) },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
            {!isMobile && isAuth && (
              <Button
                component={RouterLink}
                to="/add-post"
                variant="contained"
                startIcon={<AddRoundedIcon />}
                sx={{ mr: 1 }}
              >
                {t('nav.createJob')}
              </Button>
            )}
            <LanguageSelector compact={isMobile} />
            {isAuth ? (
              <>
                <UserTalentContainer />
                <NotificationsContainer />
                <UserContainer handleLogout={handleLogout} />
              </>
            ) : (
              !isMobile && (
                <>
                  <Button component={RouterLink} to="/login" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {t('nav.login')}
                  </Button>
                  <Button component={RouterLink} to="/register" variant="contained">
                    {t('nav.register')}
                  </Button>
                </>
              )
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 'calc(var(--app-header-height) + var(--safe-top))',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
        <FooterComponent />
        {isMobile && <Box sx={{ height: 'calc(var(--app-bottom-nav-height) + var(--safe-bottom))', flexShrink: 0 }} />}
      </Box>

      {isMobile && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (th) => th.zIndex.appBar,
            borderRadius: 0,
            borderTop: `1px solid ${brand.border}`,
            backgroundColor: alpha('#FFFFFF', 0.94),
            backdropFilter: 'saturate(180%) blur(18px)',
            WebkitBackdropFilter: 'saturate(180%) blur(18px)',
            paddingBottom: 'var(--safe-bottom)',
          }}
        >
          <BottomNavigation
            showLabels
            value={activeMobile}
            sx={{ height: 'var(--app-bottom-nav-height)', backgroundColor: 'transparent' }}
          >
            {mobileItems.map((item) => (
              <BottomNavigationAction
                key={item.to}
                component={RouterLink}
                to={item.to}
                label={item.label}
                icon={
                  item.primary ? (
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        mt: -0.5,
                        borderRadius: '14px',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#FFF',
                        background: brand.gradient,
                        boxShadow: `0 10px 20px -8px ${alpha(brand.primary, 0.8)}`,
                      }}
                    >
                      {item.icon}
                    </Box>
                  ) : (
                    item.icon
                  )
                }
                sx={{
                  minWidth: 0,
                  px: 0.5,
                  color: brand.muted,
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.7rem !important',
                    fontWeight: 700,
                    mt: item.primary ? 0.25 : 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  },
                  '&.Mui-selected': { color: 'primary.main' },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      <AICareerAssistant />
    </Box>
  );
}
